import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { setCockpitCategory } from '../../../../model'
import {
  selectCockpitCategoriesByOffering,
  selectCockpitCategory,
  selectCockpitOffering,
} from '../../../../model/selectors'
import { CockpitScopeChip, ICockpitScopeChipOption } from '../../../components/cockpitScopeChip'

import styles from './CockpitCategoryChip.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.programTypes

const ALL_CATEGORIES_KEY = 'all'

interface ICockpitCategoryChipProps {
  /** Both cockpits render the chip, so each passes the id its own tour targets it by. */
  tourId?: string
  /** `labelled` is a grey chip after a small label; `toolbar` is one segment of a toolbar that names
   *  itself, "Category: All"; `stacked` is a blue pill under its caption, as the scope pills beside
   *  it in the plan deck. */
  appearance?: 'labelled' | 'toolbar' | 'stacked'
}

/** The category of the selected programme, as its own labelled chip at the right edge of the
 *  programme row rather than a caret inside the tab. Nothing is drawn for programmes that have no
 *  categories. */
export const CockpitCategoryChip = ({
  tourId,
  appearance = 'labelled',
}: ICockpitCategoryChipProps) => {
  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const selectedOffering = useSelector(selectCockpitOffering)
  const selectedCategory = useSelector(selectCockpitCategory)
  const categoriesByOffering = useSelector(selectCockpitCategoriesByOffering)
  const categories = useMemo(
    () => categoriesByOffering[selectedOffering] ?? [],
    [categoriesByOffering, selectedOffering]
  )

  const options = useMemo<ICockpitScopeChipOption[]>(
    () => [
      { key: ALL_CATEGORIES_KEY, label: <Translation text={KEYS.allCategories} span /> },
      ...categories.map(({ category }) => ({
        key: category,
        label: <Translation text={category} span />,
      })),
    ],
    [categories]
  )

  const handleSelect = useCallback(
    (key: string) => {
      dispatch(setCockpitCategory(key === ALL_CATEGORIES_KEY ? null : key))
    },
    [dispatch]
  )

  if (categories.length === 0) {
    return null
  }

  const categoryName = selectedCategory ? (
    <Translation text={selectedCategory} span />
  ) : (
    <Translation text={KEYS.allCategories} span />
  )

  if (appearance === 'toolbar') {
    return (
      <span className={styles.toolbar} data-tour-id={tourId}>
        <CockpitScopeChip
          appearance="toolbar"
          /* A picked category stands out in its own segment; All reads as one plain line. */
          caption={selectedCategory ? <Translation text={KEYS.categoryCaption} span /> : undefined}
          value={
            selectedCategory ? (
              categoryName
            ) : (
              <Translation
                text={KEYS.categoryChip}
                /* The param takes a string, so the category's own key is resolved here. */
                replaceParams={[{ key: '{category}', value: t(KEYS.allCategories) }]}
                span
              />
            )
          }
          label={<Translation text={KEYS.categoryDropdownLabel} span />}
          ariaLabel={KEYS.categoryDropdownLabel}
          options={options}
          selectedKeys={[selectedCategory ?? ALL_CATEGORIES_KEY]}
          onSelect={handleSelect}
        />
      </span>
    )
  }

  if (appearance === 'stacked') {
    return (
      <span className={styles.stacked} data-tour-id={tourId}>
        <span className={styles.caption}>
          <Translation text={KEYS.categoryDropdownLabel} span />
        </span>

        <CockpitScopeChip
          appearance="pill"
          value={categoryName}
          label={<Translation text={KEYS.categoryDropdownLabel} span />}
          ariaLabel={KEYS.categoryDropdownLabel}
          options={options}
          selectedKeys={[selectedCategory ?? ALL_CATEGORIES_KEY]}
          onSelect={handleSelect}
        />
      </span>
    )
  }

  return (
    <span className={styles.root} data-tour-id={tourId}>
      <span className={styles.label}>
        <Translation text={KEYS.categoryDropdownLabel} span />
      </span>

      <CockpitScopeChip
        value={categoryName}
        label={<Translation text={KEYS.categoryDropdownLabel} span />}
        ariaLabel={KEYS.categoryDropdownLabel}
        options={options}
        selectedKeys={[selectedCategory ?? ALL_CATEGORIES_KEY]}
        onSelect={handleSelect}
      />
    </span>
  )
}

CockpitCategoryChip.displayName = 'CockpitCategoryChip'
